import { Injectable } from "@angular/core";
import { VideoFilterService } from "./video-filter.service";
import { VideoService } from "./video.service";
import { EnvironmentService } from "../configs/environment.service";

declare const window: any;

export interface FaceDetectionOptions {
    model: string;
    blurRadius: number;
    detectionInterval: number;
    faceTimeout: number;
    fps: number;
    inputSize: number;
    treshold: number;
}

@Injectable({
    providedIn: "root",
})
export class BlurVideoFilterService extends VideoFilterService {
    faceapi: any;
    hiddenVideo: HTMLVideoElement | null = null;
    canvas: HTMLCanvasElement | null = null;
    context: CanvasRenderingContext2D | null = null;
    detectorOpts: any;
    options: { model: string; blurRadius: number; detectionInterval: number; faceTimeout: number; fps: number; inputSize: number; treshold: number; } | null = null;
    lastDetections!: any[];
    FACE_TIMEOUT!: number;
    BLUR_DISABLED_THRESHOLD: number = 1/3;

    constructor(
        protected override videoService: VideoService,
        private environmentService: EnvironmentService
    ) {
        super(videoService);
        let BLUR_DISABLED_THRESHOLD = this.environmentService.get("BLUR_DISABLED_THRESHOLD");

        if(BLUR_DISABLED_THRESHOLD && typeof BLUR_DISABLED_THRESHOLD === "number" && !isNaN(BLUR_DISABLED_THRESHOLD) && BLUR_DISABLED_THRESHOLD >= 0 && BLUR_DISABLED_THRESHOLD <= 1) {
            this.BLUR_DISABLED_THRESHOLD = BLUR_DISABLED_THRESHOLD;
        }
    }

    modelsLoaded = false;

    async loadLibraries() {
        if (!window.faceapi) {
            return new Promise((resolve, reject) => {
                const script = document.createElement('script');
                // script.src = 'https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js'; // carico face-api.js da jsdelivr
                script.src = '/assets/models/face-api.min.js'; // carico face-api.js da local
                script.onload = () => {
                    console.log('face-api.js library loaded.');
                    this.faceapi = window.faceapi;
                    resolve(true);
                };
                script.onerror = reject;
                document.head.appendChild(script);
            });
        } else {
            console.log('face-api.js library already loaded.');
            return Promise.resolve(true);
        }
    }

    async loadModels({ modelUrl = 'https://justadudewhohacks.github.io/face-api.js/models', model = 'tiny' } = {}) {
        if (!window.faceapi) {
            await this.loadLibraries();
            // throw new Error('face-api.js is not loaded. Call loadLibraries() first.');
        }
        console.log('Loading models from:', modelUrl);
        switch (model) {
            case 'tiny':
                await this.faceapi.nets.tinyFaceDetector.loadFromUri(modelUrl);
                break;
            case 'ssd':
                await this.faceapi.nets.ssdMobilenetv1.loadFromUri(modelUrl);
                break;
            default:
                await this.faceapi.nets.tinyFaceDetector.loadFromUri(modelUrl);
                break;
        }
        //   await faceapi.nets.tinyFaceDetector.loadFromUri(modelUrl);
        this.modelsLoaded = true;
        console.log('Model loaded successfully.');
        return this.modelsLoaded;
    }

    override applyFilter(mediaStream: MediaStream, args: string[], params: { videoId?: string; options?: FaceDetectionOptions; }): MediaStream {


        this.isStarting = true;
        if (!mediaStream) {
            this.isStarting = false;
            return mediaStream;
        }
        this.originalStream = mediaStream;

        let defaultOption: FaceDetectionOptions = {
            model: 'tiny',
            blurRadius: 12,
            detectionInterval: 300,
            faceTimeout: 1000,
            fps: 25,
            inputSize: 224,
            treshold: 0.3
        }
        const videoId = params?.videoId ?? undefined;
        if (params?.options) {
            this.options = { ...defaultOption, ...params.options };
        }

        return this.startFaceDetection(videoId, this.options ?? undefined);

    }
    override removeFilter(mediaStream: MediaStream): MediaStream {
        this.isStarting = false;
        this.isFiltering = false;
        this.canvas?.remove();
        this.hiddenVideo?.remove();
        this.canvas = null;
        this.hiddenVideo = null;
        this.context = null;
        return this.originalStream ?? mediaStream;
    }

    startFaceDetection(videoId = 'video', options: FaceDetectionOptions = {
        model: 'tiny',
        blurRadius: 12,
        detectionInterval: 300,
        faceTimeout: 1000,
        fps: 25,
        inputSize: 224,
        treshold: 0.3
    }): MediaStream {

        this.options = options;
        // Creazione del canvas
        if(!this.canvas) {
            const canvas = document.createElement('canvas');
            canvas.id = 'hidden-canvas';
            canvas.style.position = 'absolute';
            canvas.style.left = '0';
            canvas.style.top = '0';
            canvas.style.pointerEvents = 'none';
            canvas.style.zIndex = '-1';
            document.body.appendChild(canvas);
            this.canvas = canvas;
        }
        
        this.context = this.canvas.getContext('2d');

        // Video “nascosto” che riceve il flusso WebRTC
        if(!this.hiddenVideo) {
            const hiddenVideo = document.createElement('video');
            hiddenVideo.style.position = 'absolute';
            hiddenVideo.style.left = '0';
            hiddenVideo.style.top = '0';
            hiddenVideo.style.width = '1px';
            hiddenVideo.style.height = '1px';
            hiddenVideo.style.opacity = '0';
            hiddenVideo.style.pointerEvents = 'none';
            hiddenVideo.style.zIndex = '-1';
            document.body.appendChild(hiddenVideo);
            this.hiddenVideo = hiddenVideo;
        }

        // Collegamento del flusso
        this.hiddenVideo.srcObject = this.originalStream;
        this.hiddenVideo.muted = true;
        this.hiddenVideo.playsInline = true;
        this.hiddenVideo.autoplay = true;

        // Parametri di buffer e detection
        this.FACE_TIMEOUT = options.faceTimeout; // millisecondi di “persistenza” del blur
        this.lastDetections = [];

        const hiddenVideo = this.hiddenVideo;
        this.hiddenVideo.addEventListener('loadedmetadata', () => {
            console.log('Hidden video metadata loaded.');
            console.log('Video Width:', hiddenVideo.videoWidth);
            console.log('Video Height:', hiddenVideo.videoHeight);

            hiddenVideo.play()
                .then(() => {
                    console.log('Hidden video is playing.');
                })
                .catch((err) => {
                    console.error('Error playing hidden video:', err);
                });
        });

        const processedStream = this.canvas.captureStream(options.fps);

        hiddenVideo.addEventListener('playing', () => {
            console.log('Hidden video is playing.');
            this.isFiltering = true;

            if (!this.originalStream) {
                this.isStarting = false;
                this.isFiltering = false;
                return;
            }

            // Inizializzo dimensioni canvas
            this.resizeCanvas();

            // Catturo e rimando in loop al video visibile
            //   const processedStream = canvas.captureStream(25);
            //   outputVideo.srcObject = processedStream;
            //   outputVideo.play();

            // Se il track video supporta l'evento resize, lo aggancio
            const [videoTrack] = this.originalStream.getVideoTracks();
            if (videoTrack && videoTrack.addEventListener) {
                videoTrack.addEventListener('resize', this.resizeCanvas);
            }

            //   const processedStream = canvas.captureStream(options.fps);

            // this.originalStream.getAudioTracks().forEach(t => processedStream.addTrack(t));

            // outputVideo.srcObject = processedStream;
            // outputVideo.muted = false;
            // outputVideo.play();

            // Detection periodica (non ogni frame)
            this.detectorOpts = options.model === 'ssd'
                ? new this.faceapi.SsdMobilenetv1Options({ minConfidence: options.treshold })
                : new this.faceapi.TinyFaceDetectorOptions({ inputSize: options.inputSize, scoreThreshold: options.treshold });

            // async function detectFacesPeriodically() {
            //     if (hiddenVideo.readyState >= 2) {
            //         const detections = await window.faceapi.detectAllFaces(hiddenVideo, detectorOpts);
            //         const now = Date.now();
            //         for (const det of detections) {
            //             lastDetections.push({ box: det.box, timestamp: now });
            //         }
            //         // Rimuovo quelle scadute
            //         lastDetections = lastDetections.filter(d => now - d.timestamp < FACE_TIMEOUT);
            //     }
            //     if (this.isFiltering) {
            //         setTimeout(detectFacesPeriodically, options.detectionInterval);
            //     }
            // }

            // Render loop
            // function render() {
            //     // controlla se la dimensione del video è cambiata
            //     if (hiddenVideo.videoWidth !== canvas.width || hiddenVideo.videoHeight !== canvas.height) {
            //         resizeCanvas();
            //     }

            //     context?.clearRect(0, 0, canvas.width, canvas.height);
            //     context?.drawImage(hiddenVideo, 0, 0, canvas.width, canvas.height);

            //     const now = Date.now();
            //     for (const det of lastDetections) {
            //         if (now - det.timestamp < FACE_TIMEOUT) {
            //             const { x, y, width, height } = det.box;
            //             applyGaussianBlur(x, y, width, height);
            //         }
            //     }

            //     requestAnimationFrame(render);
            // }

            this.detectFacesPeriodically();
            this.render();
        });


        this.originalStream?.getAudioTracks().forEach(t => processedStream.addTrack(t));

        return processedStream;
    }

    async detectFacesPeriodically() {
        if (this.hiddenVideo && this.hiddenVideo.readyState >= 2) {
            const detections = await window.faceapi.detectAllFaces(this.hiddenVideo, this.detectorOpts);
            const now = Date.now();
            for (const det of detections) {
                this.lastDetections.push({ box: det.box, timestamp: now });
            }
            // Rimuovo quelle scadute
            this.lastDetections = this.lastDetections.filter((d: any) => now - d.timestamp < this.FACE_TIMEOUT);
        }
        if (this.isFiltering) {
            setTimeout(this.detectFacesPeriodically.bind(this), this.options?.detectionInterval);
        }
    }

    render() {
        if (!this.context || !this.hiddenVideo || !this.canvas || !this.options) return;
        // controlla se la dimensione del video è cambiata
        if (this.hiddenVideo.videoWidth !== this.canvas.width || this.hiddenVideo.videoHeight !== this.canvas.height) {
            this.resizeCanvas();
        }

        this.context?.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.context?.drawImage(this.hiddenVideo, 0, 0, this.canvas.width, this.canvas.height);

        const now = Date.now();
        for (const det of this.lastDetections) {
            if (now - det.timestamp < this.FACE_TIMEOUT) {
                const { x, y, width, height } = det.box;
                if (height < this.canvas.height * this.BLUR_DISABLED_THRESHOLD) {
                    this.applyGaussianBlur(x, y, width, height);
                }

            }
        }
        if (this.isFiltering) {
            requestAnimationFrame(this.render.bind(this));
        }
    }
    applyGaussianBlur(x: number, y: number, width: number, height: number) {
        if (!this.context || !this.hiddenVideo || !this.canvas || !this.options) return;

        if (!this.context) return;
        this.context.save();
        this.context.beginPath();
        this.context.rect(x, y, width, height);
        this.context.clip();
        this.context.filter = 'blur(' + this.options.blurRadius + 'px)';
        this.context.drawImage(this.hiddenVideo, 0, 0, this.canvas.width, this.canvas.height);
        this.context.restore();
    }

    resizeCanvas() {
        if (!this.context || !this.hiddenVideo || !this.canvas || !this.options) return;

        this.canvas.width = this.hiddenVideo.videoWidth || 640;
        this.canvas.height = this.hiddenVideo.videoHeight || 480;
        console.log('Canvas resized to', this.canvas.width, this.canvas.height);
    }


}